import { useController } from 'react-hook-form';
import { InputSelectCombo, InputSelectComboProps } from 'ui/inputSelectCombo/InputSelectCombo';
import { FieldProps } from './types';

type InputSelectComboFieldProps = FieldProps &
  Omit<InputSelectComboProps, 'inputValue' | 'selectValue' | 'onChange' | 'onSelect'> & {
    /** Field name for the select value */
    selectFieldName: string;
    /** Default value for the select field */
    defaultSelectValue?: string;
    /** Transform function for input value before saving to form */
    transformInput?: (value: string) => string;
    /** Callback when input value changes (in addition to form state update) */
    onInputChange?: (value: string) => void;
    /** Callback when select value changes (in addition to form state update) */
    onSelectChange?: (value: string) => void;
  };

export const InputSelectComboField = ({
  name,
  selectFieldName,
  defaultValue = '',
  defaultSelectValue = '',
  transformInput,
  onInputChange,
  onSelectChange,
  ...rest
}: InputSelectComboFieldProps) => {
  // Controller for the input value
  const {
    field: { onChange: onInputFormChange, value: inputValue },
    fieldState: { error: inputError },
  } = useController({
    name,
    defaultValue: defaultValue,
  });

  // Controller for the select value
  const {
    field: { onChange: onSelectFormChange, value: selectValue },
    fieldState: { error: selectError },
  } = useController({
    name: selectFieldName,
    defaultValue: defaultSelectValue,
  });

  const handleInputChange = (value: string) => {
    const transformedValue = transformInput ? transformInput(value) : value;

    onInputFormChange(transformedValue);
    onInputChange?.(transformedValue);
  };

  const handleSelectChange = ([selectedValue]: string[]) => {
    onSelectFormChange(selectedValue);
    onSelectChange?.(selectedValue);
  };

  return (
    <InputSelectCombo
      {...rest}
      name={name}
      inputValue={inputValue}
      selectValue={selectValue}
      onChange={handleInputChange}
      onSelect={handleSelectChange}
      hasError={!!(inputError || selectError)}
    />
  );
};
